import React, { useEffect, useMemo } from "react";
import { useAlerts } from "./AlertContext";
import { formatTime, toDateFromFirestore } from "../../../../utils/formatters";
import { RemoveScroll } from "react-remove-scroll";
import NewBadge from "../../NewBadge";
import { useJobAlertCopy } from "../../../../hooks/useJobAlertCopy";
import type { IFirestoreAlert } from "../../../../Types/firestoreTypes";
import { handleAlertNavigation } from "../../../../utils/navigationHelpers";
import "../../../../Css/Common/Firestore/Alerts/NotificationDrawer.css";

type NotificationDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
};

function AlertBody({ alert, visible, isNew }: { alert: IFirestoreAlert; visible: boolean; isNew: boolean }) {
  const copy = useJobAlertCopy(alert, visible);
  return <>
    <div className="notification-item-titleRow">
      <h3 className="notification-item-title">{copy.title}</h3>
      {isNew ? <NewBadge /> : null}
    </div>
    <p className="notification-item-description">{copy.description}</p>
    {alert.linkAction?.link_type === "JOB" && <span className="notification-item-description">View results</span>}
  </>;
}

export const NotificationDrawer: React.FC<NotificationDrawerProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    alerts,
    isLoading,
    deleteAlert,
    deleteMultipleAlerts,
    acknowledgeAlert,
  } = useAlerts();
  const total = alerts.length;

  const sortedAlerts = useMemo(() => {
    const items = alerts.map((alert) => ({
        alert,
        time: toDateFromFirestore(alert.data.createdAt)?.getTime() ?? NaN,
      }));
    return items.sort((a, b) =>
      (Number.isFinite(b.time) ? b.time : -Infinity) -
      (Number.isFinite(a.time) ? a.time : -Infinity),
    ).map(item => item.alert);
  }, [alerts]);

  const unreadCount = useMemo(
    () =>
      alerts.filter((a) => a.data.visibility === "NEW").length,
    [alerts],
  );

  const handleDeleteAlert = async (alertId: string) => {
    await deleteAlert(alertId);
  };

  const handleDeleteAllAlerts = async () => {
    await deleteMultipleAlerts(alerts.map((a) => a.id));
  };

  const handleAcknowledgeAlert = async (alertId: string) => {
    await acknowledgeAlert(alertId);
  };

  const handleAcknowledgeAllAlerts = async () => {
    const unacknowledged = alerts
      .filter((a) => a.data.visibility !== "ACKNOWLEDGED")
      .map((a) => a.id);
    for (const id of unacknowledged) {
      await acknowledgeAlert(id);
    }

  };

  const handleAlertClick = async (alert: (typeof alerts)[0]) => {
    if (!alert.data.linkAction) return;
    await handleAcknowledgeAlert(alert.id);
    handleAlertNavigation(alert.data.linkAction, {
      alertType: alert.data.type,
    });
    onClose();
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case "CONTACT_READY":
        return <i className="fa-solid fa-user" aria-hidden />;
      case "ACTION_READY":
        return <i className="fa-solid fa-bolt" aria-hidden />;
      case "SOCIAL_PROMPT_PREFERENCE_SUGGESTION":
        return <i className="fa-solid fa-wand-magic-sparkles" aria-hidden />;
      default:
        return <i className="fa-solid fa-circle-info" aria-hidden />;
    }
  };

  const getAlertTypeClass = (type: string) => {
    switch (type) {
      case "CONTACT_READY":
        return "notification-item--contact";
      case "ACTION_READY":
        return "notification-item--action";
      case "SOCIAL_PROMPT_PREFERENCE_SUGGESTION":
        return "notification-item--action";
      default:
        return "notification-item--default";
    }
  };

  const headerSubtitle =
    total === 0
      ? "You're all caught up"
      : unreadCount > 0
        ? `${unreadCount} unread · ${total} total`
        : `${total} notification${total === 1 ? "" : "s"}`;

  return (
    <>
      {isOpen ? (
        <div
          className="notification-drawer-backdrop"
          onClick={onClose}
          aria-hidden
        />
      ) : null}
      <RemoveScroll enabled={isOpen}>
        <aside
          className={`notification-drawer${isOpen ? " notification-drawer--open" : ""}`}
          aria-label="Notifications"
          aria-hidden={!isOpen}
        >
          <header className="notification-drawer-header">
            <div className="notification-drawer-headerText">
              <p className="notification-drawer-kicker">Alerts</p>
              <h2 className="notification-drawer-title">Notifications</h2>
              <p className="notification-drawer-subtitle">{headerSubtitle}</p>
            </div>
            <div className="notification-drawer-headerActions">
              <button
                type="button"
                className="notification-drawer-close"
                onClick={onClose}
                aria-label="Close notifications"
              >
                <span className="fa-solid fa-xmark" aria-hidden />
              </button>
              {total > 0 ? (
                <div className="notification-drawer-bulkActions">
                  <button
                    type="button"
                    className="notification-drawer-textBtn notification-drawer-textBtn--read"
                    onClick={handleAcknowledgeAllAlerts}
                    disabled={isLoading || unreadCount === 0}
                  >
                    Mark all read
                  </button>
                  <button
                    type="button"
                    className="notification-drawer-textBtn notification-drawer-textBtn--clear"
                    onClick={handleDeleteAllAlerts}
                    disabled={isLoading}
                  >
                    Clear all
                  </button>
                </div>
              ) : null}
            </div>
          </header>

          <div className="notification-drawer-content">
            {isLoading && total === 0 ? (
              <div className="notification-drawer-loading" aria-busy="true">
                <div className="notification-drawer-spinner" />
                <p>Loading notifications…</p>
              </div>
            ) : total === 0 ? (
              <div className="notification-drawer-empty">
                <div className="notification-drawer-emptyIconWrap" aria-hidden>
                  <span className="fa-solid fa-bell" />
                </div>
                <p className="notification-drawer-emptyTitle">
                  No notifications
                </p>
                <span className="notification-drawer-emptyHint">
                  New alerts will show up here when something needs your
                  attention.
                </span>
              </div>
            ) : (
              <ul className="notification-drawer-list" role="list">
                {sortedAlerts.map((alert) => {
                  const isNew = alert.data.visibility === "NEW";
                  const isClickable = Boolean(alert.data.linkAction);
                  const typeClass = getAlertTypeClass(alert.data.type);

                  return (
                    <li key={alert.id} className="notification-drawer-listItem">
                      <div
                        role={isClickable ? "button" : undefined}
                        tabIndex={isClickable ? 0 : undefined}
                        className={[
                          "notification-item",
                          typeClass,
                          isNew ? "notification-item--new" : "",
                          isClickable ? "notification-item--clickable" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={
                          isClickable
                            ? () => handleAlertClick(alert)
                            : undefined
                        }
                        onKeyDown={
                          isClickable
                            ? (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  void handleAlertClick(alert);
                                }
                              }
                            : undefined
                        }
                      >
                        <div className="notification-item-icon">
                          {getAlertIcon(alert.data.type)}
                        </div>
                        <div className="notification-item-body">
                          <AlertBody alert={alert.data} visible={isOpen} isNew={isNew} />
                          {alert.data.type === "DISMISSAL_AUTOMATION" && (
                            <span>Review change</span>
                          )}
                          <time className="notification-item-time">
                            {formatTime(alert.data.createdAt)}
                          </time>
                        </div>
                        <div className="notification-item-actions">
                          {alert.data.visibility !== "ACKNOWLEDGED" ? (
                            <button
                              type="button"
                              className="notification-item-iconBtn notification-item-iconBtn--read"
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleAcknowledgeAlert(alert.id);
                              }}
                              disabled={isLoading}
                              aria-label="Mark as read"
                              title="Mark as read"
                            >
                              <span className="fa-solid fa-check" aria-hidden />
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="notification-item-iconBtn notification-item-iconBtn--delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleDeleteAlert(alert.id);
                            }}
                            disabled={isLoading}
                            aria-label="Delete notification"
                            title="Delete"
                          >
                            <span className="fa-solid fa-trash" aria-hidden />
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>
      </RemoveScroll>
    </>
  );
};

export default NotificationDrawer;
